/**
 * Phase 3.29 — Kategorische Notification-Cards.
 * Jede Card sitzt kontextuell unter dem zugehörigen Marktplatz-Toggle
 * (Direkt-Chat, Preisvorschläge, Inzahlungnahmen, Ankäufe, Auktionen).
 * Der frühere globale `NotificationsSection`-Block in DashboardPage entfällt.
 *
 * `NotificationsSection` bleibt als No-Op-Stub erhalten für
 * Backwards-Compat (Admin / Tests / sonstige Importeure).
 */
import { Input } from "../ui/input";

function CategoryNotifCard({ catKey, label, hint, currentUser, values, onChange, testid }) {
    const loginEmail = currentUser?.email || "";
    const stammPhone = currentUser?.phone_mobile || "";
    const smsEn = values?.[`notif_${catKey}_sms_enabled`];
    const smsEnVal = smsEn === undefined ? false : !!smsEn;
    const waMaster = !!values?.whatsapp_enabled;
    const waEn = !!values?.[`notif_${catKey}_whatsapp_enabled`];
    return (
        <div className="mt-3 border border-gray-200 rounded-sm p-3 bg-gray-50/60" data-testid={testid || `notif-cat-${catKey}-card`}>
            <div className="font-bold text-sm text-[#111827]">{label}</div>
            <div className="text-[11px] text-gray-500 mb-2">{hint}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-start gap-2">
                    <input
                        type="checkbox"
                        checked={true}
                        disabled
                        title="E-Mail wird immer versendet"
                        className="mt-2 w-4 h-4 accent-[#FFCC00] shrink-0"
                        data-testid={`notif-${catKey}-email-toggle`}
                    />
                    <div className="flex-1">
                        <Input
                            type="email"
                            value={values?.[`notif_${catKey}_email_addr`] || ""}
                            onChange={(e) => onChange(`notif_${catKey}_email_addr`, e.target.value)}
                            placeholder={loginEmail || "name@firma.de"}
                            className="rounded-sm font-mono-num text-xs"
                            data-testid={`notif-${catKey}-email-addr`}
                        />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">E-Mail (immer)</span>
                    </div>
                </div>
                <div className="flex items-start gap-2">
                    <input
                        type="checkbox"
                        checked={smsEnVal}
                        onChange={(e) => {
                            onChange(`notif_${catKey}_sms_enabled`, e.target.checked);
                            if (e.target.checked) onChange(`notif_${catKey}_whatsapp_enabled`, false);
                        }}
                        className="mt-2 w-4 h-4 accent-[#FFCC00] shrink-0"
                        data-testid={`notif-${catKey}-sms-toggle`}
                    />
                    <Input
                        value={values?.[`notif_${catKey}_sms_phone`] || ""}
                        onChange={(e) => onChange(`notif_${catKey}_sms_phone`, e.target.value)}
                        placeholder={stammPhone || "+49 170 …"}
                        className="rounded-sm font-mono-num text-xs"
                        disabled={!smsEnVal}
                        data-testid={`notif-${catKey}-sms-phone`}
                    />
                </div>
            </div>
            <div className="text-[11px] text-amber-700 mt-2" data-testid={`notif-${catKey}-sms-cost-hint`}>
                Hinweis: Für SMS-Benachrichtigungen entstehen Kosten gemäß aktueller Preisliste.
            </div>
            {waMaster && (
                <label className="flex items-center gap-2 mt-2 cursor-pointer text-sm" data-testid={`notif-${catKey}-whatsapp-row`}>
                    <input
                        type="checkbox"
                        checked={waEn}
                        onChange={(e) => {
                            onChange(`notif_${catKey}_whatsapp_enabled`, e.target.checked);
                            if (e.target.checked) onChange(`notif_${catKey}_sms_enabled`, false);
                        }}
                        className="w-4 h-4 accent-[#25D366] shrink-0"
                        data-testid={`notif-${catKey}-whatsapp-toggle`}
                    />
                    <span className="text-[#111827]">
                        Auch per <span className="font-bold text-[#128C7E]">WhatsApp</span> senden <span className="text-[11px] text-gray-400">(statt SMS)</span>
                        <span className="text-[11px] text-amber-700 ml-1">(Kosten gemäß aktueller Preisliste)</span>
                    </span>
                </label>
            )}
        </div>
    );
}

export function MessagesNotificationCard({ currentUser, values, onChange }) {
    return (
        <CategoryNotifCard
            catKey="messages"
            label="Nachrichten"
            hint="Chats & Direkt-Anfragen von Interessenten — Weiterleitung pro Kanal."
            currentUser={currentUser}
            values={values}
            onChange={onChange}
            testid="notif-cat-messages-card"
        />
    );
}

export function OffersNotificationCard({ currentUser, values, onChange }) {
    return (
        <CategoryNotifCard
            catKey="offers"
            label="Preisvorschläge"
            hint="Eingehende Preisvorschläge auf Ihre Inserate."
            currentUser={currentUser}
            values={values}
            onChange={onChange}
        />
    );
}

export function TradeinNotificationCard({ currentUser, values, onChange }) {
    return (
        <CategoryNotifCard
            catKey="tradein"
            label="Inzahlungnahmen"
            hint="Inzahlungnahme-Angebote auf Ihre Inserate."
            currentUser={currentUser}
            values={values}
            onChange={onChange}
        />
    );
}

export function BuyrequestsNotificationCard({ currentUser, values, onChange }) {
    return (
        <CategoryNotifCard
            catKey="buyrequests"
            label="Ankäufe"
            hint="Privatkunden-Ankaufanfragen mit passender Marke."
            currentUser={currentUser}
            values={values}
            onChange={onChange}
        />
    );
}

export function AuctionsNotificationCard({ currentUser, values, onChange }) {
    return (
        <CategoryNotifCard
            catKey="auctions"
            label="Auktionen anbieten / mitbieten"
            hint="Outbid-Alerts, Auktions-Ende, Gebote auf Ihre Auktionen."
            currentUser={currentUser}
            values={values}
            onChange={onChange}
        />
    );
}

/**
 * Backwards-Compat-Stub. Frühere Versionen rendern hier den globalen
 * Benachrichtigungs-Block. Seit Phase 3.29 sitzen alle Cards kontextuell
 * unter den jeweiligen Marktplatz-Toggles — der globale Block entfällt.
 */
export function NotificationsSection() {
    return null;
}
